import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getStorage } from "firebase/storage";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../services/firebase";
import { useRole } from "../../contexts/RoleContext";
import { fetchProviders } from "../../services/providerService";
import { useChildContext } from "../../contexts/ChildContext";
import ChildProfileFlowContent from "./shared/ChildProfileFlowContent";
import ChildMedicationManager from "./shared/ChildMedicationManager";
import MedicationEditorSheet from "./shared/MedicationEditorSheet";
import { CARE_SNAPSHOT_STEPS } from "../../constants/careSnapshotSections";
import { useAsyncForm } from "../../hooks/useAsyncForm";
import { archiveMedicationRecord, saveMedicationRecord } from "./shared/medicationPersistence";
import ChildProfileFlowPageShell from "./shared/ChildProfileFlowPageShell";
import { ACTIVE_CHILD_STORAGE_KEY } from "./shared/DashboardViewContext";
import { CARE_PROFILE_ROUTE } from "../../constants/profileRoutes";
import { buildNewChildProfilePayload } from "./childProfileForm/childProfileCreation";
import { getNextChildAccentKey } from "./shared/childAccentTheme";
import { buildChildMedicalProfileUpdate } from "./childProfileForm/medicalProfileUpdate";
import {
  archiveProfileMedicationDetail,
  saveProfileMedicationDraft,
} from "./childProfileForm/profileMedicationPersistence";
import ProfileSectionSaveFooter from "./childProfileForm/ProfileSectionSaveFooter";
import {
  getProfileSectionLabel,
  normalizeChildCondition,
} from "./childProfileForm/profileFormHelpers";
import {
  prepareChildProfileSave,
  saveChildProfilePhoto,
} from "./childProfileForm/profilePhotoSave";
import {
  createEmptyProfileFormState,
  useProfileSnapshotTracker,
} from "./childProfileForm/profileSnapshot";
import { useChildProfileFields } from "./childProfileForm/useChildProfileFields";
import { useProfileDocuments } from "./childProfileForm/useProfileDocuments";
import { useProfileMedicationEditor } from "./childProfileForm/useProfileMedicationEditor";
import {
  clearProfileDraftDisplay,
  dispatchProfileDraftDisplay,
} from "./shared/profileDraftDisplay";

const AddChildModal = ({ open: _open, onClose, onSuccess, onViewTodayMedications: _onViewTodayMedications }) => {
  const { t } = useTranslation(["terms", "common"]);
  const navigate = useNavigate();
  const storage = getStorage();
  const currentUserId = auth?.currentUser?.uid || "";
  const { childrenWithAccess, refreshRoles } = useRole();
  const { setCurrentChildId } = useChildContext();

  const activateSavedChild = (childId) => {
    if (!childId || typeof window === "undefined") return;
    window.localStorage.setItem(ACTIVE_CHILD_STORAGE_KEY, childId);
    window.dispatchEvent(new CustomEvent("captureez:set-active-child", {
      detail: { childId },
    }));
    window.dispatchEvent(new CustomEvent("captureez:active-child-changed", {
      detail: { childId },
    }));
  };

  const [stage, setStage] = useState("intake");
  const [createdChildId, setCreatedChildId] = useState("");
  const [createdChildName, setCreatedChildName] = useState("");
  const [currentStep, setCurrentStep] = useState(CARE_SNAPSHOT_STEPS.ABOUT);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    let isMounted = true;

    if (!createdChildId) {
      setProviders([]);
      return undefined;
    }

    const loadProviders = async () => {
      try {
        const nextProviders = await fetchProviders(createdChildId);
        if (isMounted) {
          setProviders(nextProviders);
        }
      } catch (error) {
        if (isMounted) {
          setProviders([]);
        }
      }
    };

    loadProviders();

    return () => {
      isMounted = false;
    };
  }, [createdChildId]);

  const [activeProfileSectionId, setActiveProfileSectionId] = useState("");
  const [activeProfileSectionLabel, setActiveProfileSectionLabel] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [documentError, setDocumentError] = useState("");

  const {
    name,
    setName,
    age,
    setAge,
    dateOfBirth,
    setDateOfBirth,
    pronouns,
    setPronouns,
    language,
    setLanguage,
    photo,
    setPhoto,
    photoURL,
    setPhotoURL,
    selectedConditions,
    setSelectedConditions,
    foodAllergies,
    setFoodAllergies,
    dietaryRestrictions,
    setDietaryRestrictions,
    sensoryIssues,
    setSensoryIssues,
    behavioralTriggers,
    setBehavioralTriggers,
    communicationNeeds,
    setCommunicationNeeds,
    careContext,
    setCareContext,
    supportProfile,
    setSupportProfile,
    resetProfileFields,
  } = useChildProfileFields();
  const {
    medicationDetails,
    setMedicationDetails,
    medicationDraft,
    editingMedicationId,
    isMedicationEditorOpen,
    updateMedicationDraft,
    clearMedicationDraft,
    addMedicationDetail,
    editMedicationDetail,
    resetMedicationEditor,
  } = useProfileMedicationEditor();
  const {
    documentSectionsRef,
    uploadedDocuments,
    openSections,
    setOpenSections,
    resetDocuments,
    renderDocumentDropZone,
  } = useProfileDocuments({
    storage,
    childId: createdChildId,
    setDocumentError,
  });
  const { hasUnsavedChanges, markCurrentSnapshot } = useProfileSnapshotTracker({
    name,
    age,
    dateOfBirth,
    photoURL,
    photoName: photo?.name || "",
    selectedConditions,
    foodAllergies,
    dietaryRestrictions,
    sensoryIssues,
    behavioralTriggers,
    communicationNeeds,
    careContext,
    supportProfile,
    medicationDetails,
    uploadedDocuments,
  });

  useEffect(() => {
    dispatchProfileDraftDisplay({ name, photoURL });
    return () => clearProfileDraftDisplay();
  }, [name, photoURL]);

  const resetForm = () => {
    clearProfileDraftDisplay();
    setStage("intake");
    setCurrentStep(CARE_SNAPSHOT_STEPS.ABOUT);
    setActiveProfileSectionId("");
    setActiveProfileSectionLabel("");
    setCreatedChildId("");
    setCreatedChildName("");
    setSubmitError("");
    setDocumentError("");
    resetProfileFields();
    resetMedicationEditor();
    resetDocuments();
    childForm.reset();
    markCurrentSnapshot(createEmptyProfileFormState());
  };

  const childForm = useAsyncForm({
    autoClose: false,
    onSuccess: () => {
      setStage("setup");
      setCurrentStep(0);
      setSubmitError("");
      setDocumentError("");
      setOpenSections({
        medical: true,
        medications: false,
        behavioral: false,
      });
    },
    onError: (error) => {
      setSubmitError(error?.message || "Could not save this profile right now.");
    },
    validate: ({ name: childName, dateOfBirth: childDateOfBirth, selectedConditions: childConditions = [] }) => {
      if (!childName?.trim()) {
        throw new Error(`Please enter the ${t("terms:profile_name").toLowerCase()}`);
      }
      if (!childDateOfBirth?.trim()) {
        throw new Error("Please enter the date of birth.");
      }
      if (!childConditions.length) {
        throw new Error("Please choose a care focus.");
      }
    },
  });

  const handleSubmit = () => {
    setSubmitError("");
    return childForm.submitForm(
      async () => {
        const auth = getAuth?.();
        const currentUserId = auth?.currentUser?.uid;

        if (!currentUserId) {
          throw new Error("User not logged in");
        }

        const docRef = await addDoc(collection(db, "children"), buildNewChildProfilePayload({
          name,
          age,
          dateOfBirth,
          selectedConditions,
          careContext,
          supportProfile,
          careOwnerId: currentUserId,
          profileColorKey: getNextChildAccentKey(childrenWithAccess),
        }));
        const childId = docRef?.id || `child-${Date.now()}`;
        let nextPhotoURL = photoURL || "";

        setCurrentChildId?.(childId);
        activateSavedChild(childId);
        await refreshRoles?.();
        clearProfileDraftDisplay();

        if (photo) {
          nextPhotoURL = await saveChildProfilePhoto({
            storage,
            childId,
            photo,
            fallbackPhotoURL: nextPhotoURL,
          });
          await updateDoc(doc(db, "children", childId), {
            photoURL: nextPhotoURL,
            profilePhoto: nextPhotoURL,
          });
          await refreshRoles?.();
          setPhotoURL(nextPhotoURL);
          setPhoto(null);
        }

        markCurrentSnapshot({
          photoURL: nextPhotoURL,
          photoName: "",
        });

        setCreatedChildId(childId);
        setCreatedChildName(name.trim());
        return { id: childId };
      },
      { name, dateOfBirth, selectedConditions }
    ).catch((error) => {
      setSubmitError(error?.message || "Could not save this profile right now.");
    });
  };

  const updateCreatedChild = async (updates) => {
    if (!createdChildId) {
      return;
    }

    await updateDoc(doc(db, "children", createdChildId), updates);
  };

  const buildMedicalProfileUpdate = (detailList = medicationDetails) => (
    buildChildMedicalProfileUpdate({
      name,
      age,
      dateOfBirth,
      photoURL,
      selectedConditions,
      foodAllergies,
      dietaryRestrictions,
      sensoryIssues,
      behavioralTriggers,
      communicationNeeds,
      careContext,
      supportProfile,
      medicationRecords: detailList,
      profileDocuments: documentSectionsRef.current,
    })
  );

  const syncMedicationProfileUpdate = (detailList) => (
    updateCreatedChild(buildMedicalProfileUpdate(detailList))
  );

  const saveCreatedChildBasics = async (targetChildId) => {
    const { savedPhotoURL, updatedChild } = await prepareChildProfileSave({
      storage,
      childId: targetChildId,
      photo,
      fallbackPhotoURL: photoURL || "",
      name,
      age,
      dateOfBirth,
      selectedConditions,
      careContext,
      medicalProfileUpdate: buildMedicalProfileUpdate(),
    });

    if (photo) {
      await refreshRoles?.();
      setPhotoURL(savedPhotoURL);
      setPhoto(null);
    }

    await updateDoc(doc(db, "children", targetChildId), updatedChild);
    setCreatedChildName(name.trim());
    setCurrentStep(0);
    markCurrentSnapshot({
      photoURL: savedPhotoURL || "",
      photoName: "",
    });
  };

  const handleSaveMedicationDraft = () => saveProfileMedicationDraft({
    medicationDetails,
    medicationDraft,
    editingMedicationId,
    childId: createdChildId,
    createdBy: auth?.currentUser?.uid,
    childNotReadyMessage: "Create the profile basics first before saving medication details.",
    setSubmitError,
    setMedicationDetails,
    clearMedicationDraft,
    persistMedicationRecord: saveMedicationRecord,
    syncMedicationDetails: syncMedicationProfileUpdate,
  });

  const handleArchiveMedication = (entry, archived = true) => archiveProfileMedicationDetail({
    medicationDetails,
    entry,
    childId: createdChildId,
    createdBy: auth?.currentUser?.uid,
    archived,
    archivedBy: auth?.currentUser?.uid || "",
    setSubmitError,
    setMedicationDetails,
    clearMedicationDraft,
    editingMedicationId,
    persistMedicationArchiveRecord: archiveMedicationRecord,
    syncMedicationDetails: syncMedicationProfileUpdate,
  });

  const handleMedicationListChanged = ({ medication } = {}) => {
    if (!medication?.id) return;

    setMedicationDetails((current) => {
      const exists = current.some((entry) => entry.id === medication.id);
      if (exists) {
        return current.map((entry) => (
          entry.id === medication.id ? medication : entry
        ));
      }
      return [...current, medication];
    });
  };

  const handleProfileStepChange = (nextStep) => {
    setActiveProfileSectionId("");
    setActiveProfileSectionLabel("");
    setCurrentStep(nextStep);
  };

  const handleProfileSectionSelect = (section) => {
    if (!section?.targetStep) return;
    setActiveProfileSectionId(section.id || "");
    setActiveProfileSectionLabel(section.label || "");
    setCurrentStep(section.targetStep);
  };

  const returnToProfileDashboard = () => {
    setCurrentStep(0);
    setActiveProfileSectionId("");
    setActiveProfileSectionLabel("");
  };

  const saveCurrentSection = async () => {
    setSubmitError("");
    try {
      let targetChildId = createdChildId;

      if (!targetChildId) {
        const created = await handleSubmit();
        targetChildId = created?.id || "";
      }

      if (!targetChildId) {
        return;
      }

      if (currentStep === CARE_SNAPSHOT_STEPS.HEALTH_CARE) {
        await updateDoc(doc(db, "children", targetChildId), {
          ...buildMedicalProfileUpdate(),
        });
        setCreatedChildName(name.trim());
        setCurrentStep(0);
        setActiveProfileSectionId("");
        setActiveProfileSectionLabel("");
        markCurrentSnapshot();
        return;
      }

      await saveCreatedChildBasics(targetChildId);
      setActiveProfileSectionId("");
      setActiveProfileSectionLabel("");
    } catch (error) {
      console.error("Failed to save child section:", error);
      setSubmitError(error?.message || "Could not save this section right now.");
    }
  };

  const sectionSaveFooter = (
    <ProfileSectionSaveFooter
      currentStep={currentStep}
      sectionLabel={activeProfileSectionLabel}
      disabled={childForm.loading}
      onSave={saveCurrentSection}
    />
  );

  const handleFinish = async () => {
    setSubmitError("");
    try {
      if (createdChildId) {
        await updateCreatedChild(buildMedicalProfileUpdate());
      }

      await refreshRoles?.();
      if (createdChildId) {
        setCurrentChildId?.(createdChildId);
        clearProfileDraftDisplay();
      }

      const payload = {
        id: createdChildId,
        name: createdChildName || name.trim(),
      };

      if (onSuccess) {
        resetForm();
        onSuccess(payload);
      } else {
        resetForm();
        if (onClose) {
          onClose();
        } else {
          navigate("/dashboard", { replace: true });
        }
      }
    } catch (error) {
      console.error("Failed to save profile details:", error);
      setSubmitError(error?.message || "Could not save the profile details right now.");
    }
  };

  const handleClose = async () => {
    if (hasUnsavedChanges && !window.confirm("You have unsaved changes. Leave this page?")) {
      return;
    }

    clearProfileDraftDisplay();
    if (onClose) {
      onClose();
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const renderMedicationDetails = () => (
    <ChildMedicationManager
      childId={createdChildId}
      childName={titleName}
      medications={medicationDetails}
      providers={providers}
      onEditMedication={editMedicationDetail}
      onAddMedication={addMedicationDetail}
      onArchiveMedication={handleArchiveMedication}
      onMedicationListChanged={handleMedicationListChanged}
      currentUserId={currentUserId}
      isChildOwner={Boolean(currentUserId)}
      canEditMedication={true}
    />
  );


  const hasProfileName = Boolean(createdChildName || name);
  const titleName = createdChildName || name || "New profile";
  const createdChild = { id: createdChildId, name: titleName, careContext, conditions: selectedConditions };
  const activeSectionLabel = activeProfileSectionLabel || getProfileSectionLabel(currentStep);

  return (
    <ChildProfileFlowPageShell
      title={currentStep === 0 ? (hasProfileName ? `${titleName}'s care snapshot` : titleName) : activeSectionLabel}
      subtitle={currentStep === 0 ? "" : titleName}
      onBack={currentStep === 0 ? handleClose : returnToProfileDashboard}
      footer={currentStep > 0 && currentStep < CARE_SNAPSHOT_STEPS.REVIEW ? sectionSaveFooter : null}
    >
      <ChildProfileFlowContent
        mode="add"
        stage={stage}
        currentStep={currentStep}
        onStepChange={handleProfileStepChange}
        submitError={submitError}
        setSubmitError={setSubmitError}
        documentError={documentError}
        setDocumentError={setDocumentError}
        name={name}
        setName={setName}
        age={age}
        setAge={setAge}
        dateOfBirth={dateOfBirth}
        setDateOfBirth={setDateOfBirth}
        pronouns={pronouns}
        setPronouns={setPronouns}
        language={language}
        setLanguage={setLanguage}
        photo={photo}
        setPhoto={setPhoto}
        photoURL={photoURL}
        setPhotoURL={setPhotoURL}
        selectedConditions={selectedConditions}
        setSelectedConditions={setSelectedConditions}
        normalizeCondition={normalizeChildCondition}
        foodAllergies={foodAllergies}
        setFoodAllergies={setFoodAllergies}
        dietaryRestrictions={dietaryRestrictions}
        setDietaryRestrictions={setDietaryRestrictions}
        sensoryIssues={sensoryIssues}
        setSensoryIssues={setSensoryIssues}
        behavioralTriggers={behavioralTriggers}
        setBehavioralTriggers={setBehavioralTriggers}
        communicationNeeds={communicationNeeds}
        setCommunicationNeeds={setCommunicationNeeds}
        careContext={careContext}
        setCareContext={setCareContext}
        supportProfile={supportProfile}
        setSupportProfile={setSupportProfile}
        childId={createdChildId}
        child={createdChild}
        renderDocumentDropZone={renderDocumentDropZone}
        renderMedicationDetails={renderMedicationDetails}
        medicationDetails={medicationDetails}
        providers={providers}
        careTeamMemberCount={0}
        activeProfileSectionId={activeProfileSectionId}
        activeProfileSectionLabel={activeSectionLabel}
        childDisplayName={titleName}
        onProfileSectionSelect={handleProfileSectionSelect}
        onOpenCareTeam={createdChildId ? () => navigate("/care-team") : null}
        onOpenProviders={createdChildId ? () => navigate("/providers", {
          state: {
            childId: createdChildId,
            returnTo: CARE_PROFILE_ROUTE,
          },
        }) : null}
        uploadedDocuments={uploadedDocuments}
        openSections={openSections}
        setOpenSections={setOpenSections}
        createdChildName={createdChildName}
        isSubmitting={childForm.loading}
        onCreate={handleSubmit}
        onEnsureChildId={handleSubmit}
        onFinish={handleFinish}
        onClose={handleClose}
        t={t}
      />

      <MedicationEditorSheet
        open={isMedicationEditorOpen}
        onClose={clearMedicationDraft}
        childName={titleName}
        child={createdChild}
        draft={medicationDraft}
        isEditing={Boolean(editingMedicationId)}
        onDraftChange={updateMedicationDraft}
        onSaveDraft={handleSaveMedicationDraft}
        onClearDraft={clearMedicationDraft}
        providers={providers}
      />
    </ChildProfileFlowPageShell>
  );
};

export default AddChildModal;
